/**
 * API-Client des Frontends.
 *
 * Eine einzige Stelle spricht mit dem Backend: Basis-URL, Sitzungstoken (Bearer) und
 * einheitliche Fehlerbehandlung. Der Token liegt im localStorage und überlebt einen
 * Reload; bei 401 wird er verworfen und die App fällt zur Anmeldung zurück.
 *
 * Demo-Modus: Wird der Client mit `--mode demo` gebaut (VITE_DEMO=1), spricht die App
 * NICHT mit einem Server, sondern zeigt Beispieldaten. So bleibt die klickbare Vorschau
 * ohne Backend lauffähig. Im normalen Build ist DEMO false und alles läuft live.
 */

export const DEMO = import.meta.env.VITE_DEMO === '1';

/** Same-Origin `/api` im Betrieb; per VITE_API_URL überschreibbar (z. B. getrennter Host). */
const BASIS = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/$/, '');

const TOKEN_KEY = 'nkp_token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* localStorage kann in seltenen Kontexten fehlen — Token dann nur im Speicher. */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignorieren */
  }
}

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiFehler';
  }
}

interface Optionen {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
}

/** Ereignis, wenn eine bestehende Sitzung vom Server abgelehnt wird (401). */
export const SITZUNG_ABGELAUFEN = 'grundpuls:sitzung-abgelaufen';

const PLATTFORM = 'web';
const MAX_WIEDERHOLUNGEN = 8;
const warte = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Lesende Anfragen werden bei 503 (kurzer Datenbank-Aussetzer, Neustart) und Netzfehlern
 * mit wachsendem Abstand wiederholt, bis zu 8-mal — der Nutzer bleibt angemeldet.
 * Schreibende Anfragen werden nie automatisch wiederholt.
 */
export async function apiFetch<T = unknown>(pfad: string, opt: Optionen = {}): Promise<T> {
  const methode = opt.method ?? 'GET';
  const wiederholbar = methode === 'GET';
  for (let versuch = 0; ; versuch++) {
    const token = getToken();
    let res: Response;
    try {
      res = await fetch(BASIS + pfad, {
        method: methode,
        headers: {
          ...(opt.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Platform': PLATTFORM,
        },
        body: opt.body !== undefined ? JSON.stringify(opt.body) : undefined,
      });
    } catch {
      if (wiederholbar && versuch < MAX_WIEDERHOLUNGEN) {
        await warte(Math.min(400 * 2 ** versuch, 5000));
        continue;
      }
      throw new ApiFehler(0, 'Keine Verbindung zum Server. Bitte die Internetverbindung prüfen.');
    }
    if (res.status === 503 && wiederholbar && versuch < MAX_WIEDERHOLUNGEN) {
      const sekunden = Number(res.headers.get('Retry-After')) || 0;
      await warte(Math.max(sekunden * 1000, Math.min(400 * 2 ** versuch, 5000)));
      continue;
    }

    const text = await res.text();
    let daten: unknown = {};
    if (text) {
      try {
        daten = JSON.parse(text);
      } catch {
        daten = { fehler: text };
      }
    }

    if (res.status === 401 && token) {
      clearToken();
      window.dispatchEvent(new Event(SITZUNG_ABGELAUFEN));
    }
    if (!res.ok) {
      const d = daten as { fehler?: string; details?: unknown };
      throw new ApiFehler(res.status, d.fehler ?? `Anfrage fehlgeschlagen (${res.status}).`, d.details);
    }
    return daten as T;
  }
}

export const api = {
  get: <T = unknown>(pfad: string) => apiFetch<T>(pfad),
  post: <T = unknown>(pfad: string, body?: unknown) => apiFetch<T>(pfad, { method: 'POST', body }),
  patch: <T = unknown>(pfad: string, body?: unknown) => apiFetch<T>(pfad, { method: 'PATCH', body }),
  del: <T = unknown>(pfad: string) => apiFetch<T>(pfad, { method: 'DELETE' }),
};

/* ------------------------------------------------------------------ Anmeldung */

export interface Benutzer {
  id: string;
  email: string;
  name: string | null;
  vorname: string | null;
  nachname: string | null;
  rolle: string;
  istAdmin?: boolean;
  passwortGesetzt: boolean;
  passwortAendern: boolean;
  onboardingErforderlich: boolean;
  googleVerknuepft: boolean;
}
export interface Mandant {
  id: string;
  stufe: number;
  name: string;
  typ: string;
  testphaseBis: string | null;
}

export interface Anmeldearten {
  google: boolean;
  registrierung: boolean;
}

/** Antwort, wenn ein Code versendet wurde; testCode nur in der Testphase ohne E-Mail-Versand. */
export interface CodeVersand {
  testCode?: string;
}

/** Welche Anmeldewege der Server anbietet. */
export async function anmeldearten(): Promise<Anmeldearten> {
  if (DEMO) return { google: true, registrierung: true };
  return api.get('/auth/anbieter');
}

/** Empfehlungscode aus der aktuellen Adresse (ohne Speicherung — kein Einwilligungsbanner). */
export function empfehlungscode(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const ref = new URLSearchParams(window.location.search).get('ref');
  return ref && /^[A-Za-z0-9_.-]{1,64}$/.test(ref) ? ref : undefined;
}

/** Startadresse der Google-Anmeldung (vollständiger Seitenwechsel). */
export function googleStartUrl(): string {
  const ref = empfehlungscode();
  return `${BASIS}/auth/google${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`;
}

/**
 * Rückkehr von Google: Einmal-Code (?login_code=) gegen den Sitzungstoken tauschen und
 * die Adresse sofort bereinigen. Liefert eine Fehlerkennung, falls die Anmeldung scheiterte.
 */
export async function anmeldungAusAdresseUebernehmen(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  const code = p.get('login_code');
  const fehler = p.get('login_fehler');
  if (!code && !fehler) return null;
  p.delete('login_code');
  p.delete('login_fehler');
  const rest = p.toString();
  window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
  if (fehler) return /^[a-z_]{1,40}$/.test(fehler) ? fehler : 'ungueltig';
  try {
    const r = await api.post<{ token: string }>('/auth/exchange-code', { code });
    setToken(r.token);
    return null;
  } catch {
    return 'abgelaufen';
  }
}

export type KontoStatus = 'login' | 'set_password' | 'register';

export async function emailPruefen(email: string): Promise<KontoStatus> {
  return (await api.post<{ status: KontoStatus }>('/auth/check-email', { email })).status;
}

export async function registrieren(daten: {
  email: string;
  password: string;
  firstName: string;
  lastName?: string;
  firma?: string;
}): Promise<CodeVersand> {
  return api.post('/auth/register-email', { ...daten, ref: empfehlungscode() });
}

async function mitToken(pfad: string, body: unknown): Promise<void> {
  const r = await api.post<{ token: string }>(pfad, body);
  setToken(r.token);
}

export const emailBestaetigen = (email: string, code: string) => mitToken('/auth/verify-email', { email, code });
export const codeErneutSenden = (email: string) => api.post<CodeVersand>('/auth/resend-code', { email });
export const anmeldenMitPasswort = (email: string, password: string) =>
  mitToken('/auth/login-email', { email, password });
export const passwortVergessen = (email: string) => api.post<CodeVersand>('/auth/passwort-vergessen', { email });
export const passwortZuruecksetzen = (email: string, code: string, password: string) =>
  mitToken('/auth/passwort-zuruecksetzen', { email, code, password });
export const passwortAendern = (altesPasswort: string | undefined, neuesPasswort: string) =>
  mitToken('/auth/passwort-aendern', { altesPasswort, neuesPasswort });
export const emailAendernAnfordern = (newEmail: string) =>
  api.post<CodeVersand>('/auth/change-email/request', { newEmail });
export const emailAendernBestaetigen = (code: string) => mitToken('/auth/change-email/confirm', { code });
export const onboardingAbschliessen = (typ: 'privatvermieter' | 'verwaltung', firma?: string) =>
  api.post('/auth/onboarding', { typ, firma });

export async function ich(): Promise<{ benutzer: Benutzer; mandant: Mandant }> {
  return api.get('/auth/user');
}

export async function abmelden(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    /* Auch wenn der Server nicht erreichbar ist: lokal abmelden. */
  }
  clearToken();
}

/** Beendet alle Sitzungen dieses Kontos auf allen Geräten. */
export async function ueberallAbmelden(): Promise<void> {
  await api.post('/auth/logout-alle');
  clearToken();
}
